/**
 * Insight automático DataFlow — narrativa executiva de tendências e impacto.
 */

import { shouldShowCount } from './tplSeverityStyles';

export interface DataflowInsightInput {
  disciplinaPct: number;
  prevDisciplinaPct?: number | null;
  ielScore: number;
  topLossLabels: string[];
  topLossCounts: number[];
  totalLosses: number;
  criticalRoutineTitles?: string[];
  mainRiskLabel?: string | null;
}

export function computeDataflowInsights(input: DataflowInsightInput): string[] {
  const insights: string[] = [];
  const {
    disciplinaPct,
    prevDisciplinaPct,
    ielScore,
    topLossLabels,
    topLossCounts,
    totalLosses,
    criticalRoutineTitles = [],
    mainRiskLabel,
  } = input;

  if (prevDisciplinaPct != null) {
    const delta = disciplinaPct - prevDisciplinaPct;
    if (delta <= -5) {
      insights.push(
        `A disciplina caiu ${Math.abs(delta)} pontos nas últimas duas semanas.`,
      );
    } else if (delta >= 5) {
      insights.push(`A disciplina subiu ${delta} pontos — tendência positiva.`);
    }
  }

  if (topLossLabels.length >= 2 && totalLosses > 0) {
    const topSum = topLossCounts.slice(0, 3).reduce((s, c) => s + c, 0);
    const pct = Math.round((topSum / totalLosses) * 100);
    if (pct >= 50) {
      const names = topLossLabels.slice(0, 3).join(', ');
      insights.push(`${pct}% das perdas estão concentradas em ${topLossLabels.length >= 3 ? '3 rotinas' : 'rotinas-chave'}: ${names}.`);
    }
  } else if (topLossLabels.length === 1 && totalLosses > 0) {
    const pct = Math.round(((topLossCounts[0] ?? 0) / totalLosses) * 100);
    if (pct >= 40) {
      insights.push(`${pct}% das perdas concentram-se em ${topLossLabels[0]}.`);
    }
  }

  if (criticalRoutineTitles.length > 0 && disciplinaPct < 75) {
    const projected = Math.min(100, ielScore + Math.round(criticalRoutineTitles.length * 4));
    insights.push(
      `Se as rotinas críticas forem normalizadas, o IEL projetado sobe de ${ielScore} para ${projected}.`,
    );
  }

  if (disciplinaPct < 70) {
    insights.push(
      'O principal fator de degradação atual é a não execução das rotinas de liderança.',
    );
  } else if (mainRiskLabel) {
    insights.push(`Fator prioritário: ${mainRiskLabel}.`);
  }

  if (insights.length === 0) {
    insights.push('Operação dentro do esperado — manter ritmo de gemba e camada.');
  }

  return insights.slice(0, 4);
}

/** Estimativa de impacto financeiro (interpretação frontend, sem API). */
export function estimateLossFinancialImpact(lossCount: number, criticalCount = 0): number {
  if (!shouldShowCount(lossCount)) return 0;
  const baseCost = 120;
  const criticalMultiplier = 1.8;
  return Math.round(lossCount * baseCost + criticalCount * baseCost * criticalMultiplier);
}
